import React, { useEffect, useState, useCallback, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  CheckCircle, XCircle, Warning, SpinnerGap, SignIn, ArrowRight,
} from "@phosphor-icons/react";

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

const fmtINR = (n) => {
  try {
    return new Intl.NumberFormat("en-IN", {
      style: "currency", currency: "INR", maximumFractionDigits: 2,
    }).format(Number(n) || 0);
  } catch { return `₹${n}`; }
};

export default function ApprovalLanding() {
  const q = useQuery();
  const navigate = useNavigate();
  const token = q.get("t") || "";
  const action = (q.get("a") || "approve").toLowerCase();
  const [state, setState] = useState({ loading: true, error: null, data: null, acting: false, result: null });
  // Guards: we only want to redirect to /login ONCE and only auto-execute ONCE
  // (React StrictMode double-fires effects in dev which otherwise causes a
  // nested-redirect loop like /login?next=/login?next=/approve...).
  const redirected = useRef(false);
  const executed   = useRef(false);

  const load = useCallback(async () => {
    if (!token) {
      setState((s) => ({ ...s, loading: false, error: "Missing token in URL." }));
      return;
    }
    try {
      const r = await api.get(`/approvals/token/${token}`);
      setState((s) => ({ ...s, loading: false, data: r.data, error: null }));
    } catch (e) {
      const status = e?.response?.status;
      if (status === 401 || status === undefined) {
        if (!redirected.current) {
          redirected.current = true;
          const here = encodeURIComponent(window.location.pathname + window.location.search);
          navigate(`/login?next=${here}`, { replace: true });
        }
        return;
      }
      setState((s) => ({
        ...s, loading: false,
        error: e?.response?.data?.detail || "Unable to load this approval link.",
      }));
    }
  }, [token, navigate]);

  useEffect(() => { load(); }, [load]);

  // Auto-execute the moment the page loads IF the user is signed in as the
  // correct approver and the token hasn't been used. The user explicitly asked
  // for "approve/reject should happen from the mail click itself" — clicking
  // the email button lands here and the action fires automatically.
  useEffect(() => {
    const d = state.data;
    if (!d) return;
    if (d.used_at) return;       // already used
    if (!d.auth_match) return;   // wrong account
    if (state.acting || state.result) return;   // already running / done
    if (executed.current) return;
    executed.current = true;
    execute();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.data]);

  const execute = async () => {
    setState((s) => ({ ...s, acting: true }));
    try {
      const r = await api.post("/approvals/act-via-token", {
        token,
        app_url: window.location.origin,
      });
      setState((s) => ({ ...s, acting: false, result: r.data }));
    } catch (e) {
      setState((s) => ({
        ...s, acting: false,
        error: e?.response?.data?.detail || "Action failed.",
      }));
    }
  };

  const { loading, error, data, acting, result } = state;
  const docs = data?.docs || [];
  const isPayment = data?.doc_type === "payment_request";
  const noun = isPayment ? "payment" : "PO";
  const totalAmt = docs.reduce((s, d) => s + (Number(d.amount) || 0), 0);

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 flex items-start justify-center p-4 sm:p-8">
      <div className="w-full max-w-2xl">
        {/* Header */}
        <div className="mb-6 text-center">
          <div className="inline-flex items-center gap-2">
            <div className="h-8 w-8 grid place-items-center rounded-sm bg-gradient-to-br from-red-500 to-red-700 shadow-[0_0_18px_rgba(239,68,68,0.55)]">
              <span className="text-white font-black text-sm">₹</span>
            </div>
            <span className="font-heading font-black text-base tracking-tight bg-gradient-to-r from-red-400 via-amber-300 to-cyan-300 bg-clip-text text-transparent">
              RECONIK
            </span>
          </div>
          <h1 className="mt-3 text-xl sm:text-2xl font-bold text-cyan-50">
            One-click {action === "reject" ? "Rejection" : "Approval"}
          </h1>
        </div>

        <div className="rounded-lg border border-slate-800 bg-slate-900 p-6" data-testid="approval-landing-card">
          {loading && (
            <div className="text-center py-12 text-slate-400">
              <SpinnerGap size={32} weight="bold" className="mx-auto animate-spin mb-3 text-cyan-400" />
              Loading approval details…
            </div>
          )}

          {!loading && error && (
            <div className="text-center py-8" data-testid="approval-error">
              <XCircle size={40} weight="duotone" className="mx-auto text-rose-400 mb-3" />
              <div className="text-base font-semibold text-rose-200 mb-1">{error}</div>
              <div className="text-xs text-slate-500 mb-5">
                If the link is expired, open the dashboard to act manually.
              </div>
              <Button onClick={() => navigate("/dashboard")} className="bg-cyan-600 hover:bg-cyan-500">
                Open Dashboard <ArrowRight size={14} weight="bold" className="ml-1" />
              </Button>
            </div>
          )}

          {!loading && data && !error && (
            <>
              {/* Identity panel */}
              <div
                className={
                  "rounded-md border px-4 py-3 mb-5 flex items-start gap-2 " +
                  (data.auth_match
                    ? "bg-emerald-950/40 border-emerald-800 text-emerald-200"
                    : "bg-rose-950/40 border-rose-800 text-rose-200")
                }
                data-testid="approval-identity-panel"
              >
                {data.auth_match
                  ? <CheckCircle size={16} weight="duotone" className="shrink-0 mt-0.5" />
                  : <Warning size={16} weight="duotone" className="shrink-0 mt-0.5" />}
                <div className="text-xs leading-relaxed">
                  {data.auth_match ? (
                    <>Signed in as <b>{data.session_email}</b> — matches the approver this link was issued for.</>
                  ) : (
                    <>This link was issued for <b>{data.approver_email}</b>, but you're signed in as <b>{data.session_email || "(no one)"}</b>. Please sign in with the correct account.</>
                  )}
                </div>
              </div>

              {/* Already used */}
              {data.used_at && (
                <div className="rounded-md border border-slate-700 bg-slate-950 px-4 py-3 mb-5 text-xs text-slate-300" data-testid="approval-already-used">
                  This link was already used on <b>{new Date(data.used_at).toLocaleString()}</b>{data.used_by ? <> by <b>{data.used_by}</b></> : null}.
                </div>
              )}

              {/* Summary */}
              <div className="mb-4 flex items-center justify-between flex-wrap gap-2">
                <div className="text-xs uppercase tracking-wider font-bold text-slate-400">
                  {docs.length} {noun}{docs.length === 1 ? "" : "s"} · Total {fmtINR(totalAmt)}
                </div>
                <div
                  className={
                    "inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider border " +
                    (action === "reject"
                      ? "bg-rose-950/40 border-rose-700 text-rose-200"
                      : "bg-emerald-950/40 border-emerald-700 text-emerald-200")
                  }
                  data-testid="approval-action-pill"
                >
                  Action: {action}
                </div>
              </div>

              {/* PO cards */}
              <div className="space-y-3 mb-5" data-testid="approval-po-list">
                {docs.map((d) => (
                  <div
                    key={d.id}
                    className="rounded-md border border-slate-700 bg-slate-950/60 p-4"
                    data-testid={`approval-po-${d.id}`}
                  >
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                      <div>
                        <div className="font-bold text-cyan-50">{d.vendor || "—"}</div>
                        <div className="text-xs text-slate-400">
                          {isPayment ? "Payment · PO/Ref " : "PO "}<span className="text-slate-200 font-mono">{d.po_number || d.manual_po_ref || "—"}</span> ·
                          {" "}{d.po_date || "—"} · {d.outlet || "—"}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-base font-bold text-amber-300 font-mono">{fmtINR(d.amount)}</div>
                        <div className="text-[10px] uppercase tracking-wider text-slate-500 mt-0.5">
                          L{d.current_level || 1}/{d.total_levels || 1} · {d.pending_role_label || d.pending_role_id || "—"}
                        </div>
                      </div>
                    </div>
                    {Array.isArray(d.line_items) && d.line_items.length > 0 && (
                      <div className="mt-3 rounded-sm border border-slate-800 bg-slate-900 overflow-hidden">
                        <table className="w-full text-xs">
                          <thead>
                            <tr className="bg-slate-800/60 text-slate-400 text-[10px] uppercase tracking-wider">
                              <th className="text-left px-3 py-1.5">Product</th>
                              <th className="text-right px-3 py-1.5">Qty</th>
                              <th className="text-right px-3 py-1.5">Rate</th>
                              <th className="text-right px-3 py-1.5">Amount</th>
                            </tr>
                          </thead>
                          <tbody>
                            {d.line_items.slice(0, 6).map((li, i) => (
                              <tr key={i} className="border-t border-slate-800">
                                <td className="px-3 py-1.5 text-slate-300 truncate max-w-[180px]" title={li.product}>{li.product || "—"}</td>
                                <td className="px-3 py-1.5 text-right font-mono text-slate-400">{li.qty ?? "—"}</td>
                                <td className="px-3 py-1.5 text-right font-mono text-slate-400">{li.rate ? fmtINR(li.rate) : "—"}</td>
                                <td className="px-3 py-1.5 text-right font-mono font-semibold text-cyan-200">{li.amount ? fmtINR(li.amount) : "—"}</td>
                              </tr>
                            ))}
                            {d.line_items.length > 6 && (
                              <tr><td colSpan={4} className="px-3 py-1.5 text-center text-[10px] text-slate-500">… and {d.line_items.length - 6} more line(s)</td></tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* Action area */}
              {result ? (
                <div className="rounded-md border border-emerald-800 bg-emerald-950/40 px-4 py-4 text-center" data-testid="approval-result">
                  <CheckCircle size={32} weight="duotone" className="mx-auto text-emerald-400 mb-2" />
                  <div className="text-base font-semibold text-emerald-100 mb-1">
                    {action === "reject" ? "Rejected" : "Approved"} successfully.
                  </div>
                  <div className="text-xs text-slate-400 mb-4">
                    {isPayment ? (result?.result?.message || "Done")
                      : <>Updated <b>{result?.result?.updated || docs.length}</b> · advanced <b>{result?.result?.advanced || 0}</b>.</>}
                  </div>
                  <Button onClick={() => navigate("/dashboard")} className="bg-cyan-600 hover:bg-cyan-500" data-testid="approval-result-dashboard">
                    Open Dashboard <ArrowRight size={14} weight="bold" className="ml-1" />
                  </Button>
                </div>
              ) : data.used_at ? null : !data.auth_match ? (
                <Button
                  onClick={() => {
                    const here = encodeURIComponent(window.location.pathname + window.location.search);
                    navigate(`/login?next=${here}`);
                  }}
                  className="w-full h-11 bg-cyan-600 hover:bg-cyan-500"
                  data-testid="approval-signin-btn"
                >
                  <SignIn size={16} weight="bold" className="mr-2" />
                  Sign in as {data.approver_email}
                </Button>
              ) : (
                <div
                  className={
                    "rounded-md border px-4 py-4 text-center " +
                    (action === "reject"
                      ? "bg-rose-950/40 border-rose-800 text-rose-100"
                      : "bg-cyan-950/40 border-cyan-800 text-cyan-100")
                  }
                  data-testid="approval-auto-acting"
                >
                  <SpinnerGap size={28} weight="bold" className="mx-auto animate-spin mb-2" />
                  <div className="text-sm font-semibold">
                    {action === "reject" ? "Rejecting…" : "Approving…"}
                  </div>
                  <div className="text-[11px] text-slate-400 mt-1">
                    Processing action automatically — please don't close this tab.
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
